import moment from 'moment';
import { rangeIndexesToString } from 'shared/excel/excel';
import { MultipleErrorsError} from 'shared/utils/utils';


export class ConversionError extends MultipleErrorsError {
  constructor(errors, message = '') {
    super(
      errors.map(({ rowIndex, columnIndex, error, sheet }) =>
        `${error} at ${rangeIndexesToString({ rowIndex, columnIndex, sheet })}`
      ), 
      message || `${errors.length} conversion errors were detected`
    );
  }
}

// See: https://help.salesforce.com/s/articleView?id=000325035&type=1
// 'Date' field acceptable formats
//     YYYY-MM-DD                 - covered by Excel conversion to double
//     YYYY-MM-DD hh:mm:ss        - covered by Excel conversion to double
//     YYYY-MM-DDThh:mm:ssZ       - need to check against regexp; no conversion req
//     YYYY-MM-DDThh:mm:ss.sssZ   - need to check against regexp; no conversion req
// 'Date Time' field acceptable formats
//     YYYY-MM-DD hh:mm:ss        - covered by Excel conversion to double
//     YYYY-MM-DDThh:mm:ssZ       - need to check against regexp; no conversion req
//     YYYY-MM-DDThh:mm:ss.sssZ   - need to check against regexp; no conversion req
// 'Time' field acceptable formats
//     hh:mm:ss.sssZ (For example, 07:00:00.000Z) - need to check against regexp; no conversion req

export function getLocalDateTimeRegex() { return /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?)(?!Z|\.\d{3}Z)/g; }
// Safari doesn't support lookbehind RegExp's as of v.16.0
// export function getLocalTimeRegex() { return /(?<!\d{4}-\d{2}-\d{2}T)(\d{2}:\d{2}:\d{2}(?:\.\d{3})?)(?!Z|\.\d{3}Z)/g; }

export function getAnyDateTimeRegex() { return /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z?)/g; }
export function getAnyDateRegex() { return /(\d{4}-\d{2}-\d{2})(?!T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z?)/g; }
// Safari doesn't support lookbehind RegExp's as of v.16.0
// export function getAnyTimeRegex() { return /(?<!\d{4}-\d{2}-\d{2}T)(\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z?)/g; }

// Safari doesn't support lookbehind RegExp's as of v.16.0
// export function getZuluTimeRegex() { return /(?<!\d{4}-\d{2}-\d{2}T)(\d{2}:\d{2}:\d{2}\.\d{3}Z)/g; }
export function getZuluTimeNonStrictRegex() { return /\S*(\d{2}:\d{2}:\d{2}\.\d{3}Z)/g; }
export function getZuluDateTimeRegex() { return /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z)/g; }

function checkForZuluTime(value) {
  const m = value.match(getZuluTimeNonStrictRegex());
  return m && m[0].length === 13; // 12:12:12.000Z - 13 chars
}

function checkForZuluDateTime(value) {
  return !!value.match(getZuluDateTimeRegex());
}

export function bypassConverter(item) { return { value: item }; }
// TODO: Check: do we even need a converted for doubles?
// doubleRegExp = /^(?<integer>[+\-]?\d+).(?<fraction>\d+)$/;

// See https://www.exceltactics.com/definitive-guide-using-dates-times-excel/
export function dateTimeNumberToMoment(item) {
  // Perform roundin to avoid loosing of one second in some cases
  // 5:00:00 is 17999.9999971 when converted to unix time at least in Excel365 (should be 18000)
  const unixTime = Math.round((item - 25569) * 86400);
  return moment.unix(unixTime).utc();
}

function convertDT(value, checkFn, formatString, errorString) {
  const number = +value;
  if (Number.isNaN(number)) {
    if (typeof(value) === 'string' && checkFn(value)) {
      return { value };
    }
  }
  else {
    return { value: dateTimeNumberToMoment(number).format(formatString) };
  }

  return { error: `'${value}' is not a valid Zulu ${errorString}`, value: null };
}

export function dateTimeConverter(item) {
  return convertDT(item, checkForZuluDateTime, moment.HTML5_FMT.DATETIME_LOCAL_SECONDS, 'dateTime');
}

export function timeConverter(item) {
  return convertDT(item, checkForZuluTime, 'HH:mm:ss.SSS[Z]', 'time');
}

export function dateConverter(item) {
  // INGORE IT:
  // Convert to YYYY-MM-DDThh:mm:ss - to not loose one day if tz is not GMT. 
  // Example: Timezone is GMT+8
  // Data sent to our server must be one of these:
  //     2011-01-10T09:00:00        - converted to YYYY-MM-DDTmm:hh:ss.SSS[Z] with respect to timezone
  //     2011-01-10T09:00:00Z       - sent to SF as is
  //     2011-01-10T09:00:00.000Z   - sent to SF as is 
  // So that if you set the hh:mm:ss to 00:00:00 or format the date as YYYY-MM-DD, 
  // the date in Salesforce will be set to the previous day ('2011-01-09' instead of '2011-01-10'): 9 hours will be lost
  // IGNORE IT END.

  return convertDT(item, checkForZuluDateTime, moment.HTML5_FMT.DATE, 'date'); 
}

export function ensureNumberConverter(type, item) {
  const value = +item;
  return isNaN(value)
    ? { error: `'${item}' is not a valid ${type}`, value: null }
    : { value };
}


